import React, {useEffect, useMemo, useState} from 'react';
import {Divider, Form, Input, Select, Space} from 'antd';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {
  StyledExpenseDetails,
  StyledExpenseDetailsButton,
} from './StyledExpenseDetails';
import AgentAlert from '../../../../../AgentAlert';
import UserDetails from '../../../../../UserDetails';
import SuggestionPopover from '../../../SuggestionPopover';
import CompanyTagSelect from '../../../../TransactionsPage/CompanyTagSelect/CompanyTagSelect';
import DetailsTable from '../../../../TransactionsPage/DetailsTable';
import MissingFieldIndicator from '../../../../../MissingFieldIndicator';
import SubscriptionIconWithLink from '../../../../SubscriptionPage/SubscriptionIconWithLink';
import {expensesHelpers, formHelpers} from '../../../../../../utils/helpers';
import {StarsIcon} from '../../../../../../icons';
import {useHasAccess, useIsAdvancedExpensesPlan} from '../../../../../../hooks';
import {
  agentTypeConstants,
  tagFamilyConstants
} from '../../../../../../constants';
import {agentActions} from '../../../../../../state/actions/agent.actions';

const formId = 'expense-details-form';

const ExpenseDetails = ({
  availableLink = false,
  children,
  displayMissingFieldIndicator = false,
  expense,
  onEdit,
  subscription,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses']);
  const [initialFormValues, setInitialFormValues] = useState({
    category_tags: [],
    description: '',
    location_tags: [],
    policy_expense_type: undefined,
    project_tags: [],
    tags: [],
    team_tags: [],
  });
  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const isAdvancedExpensesPlan = useIsAdvancedExpensesPlan();

  const {employeeEmail, expenseTags, isAdmin} = useSelector(state => ({
    employeeEmail: state?.user?.employee?.email,
    expenseTags: state?.company?.expenseTags || [],
    isAdmin: state?.user.isAdmin
  }));

  const isDraftExpense = useMemo(() => {
    return expensesHelpers.isExpenseDraft(expense);
  }, [expense?.review_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = useHasAccess('PUT_EXPENSE_UPDATE');

  const expenseCategoryOptions = useMemo(() => formHelpers.getTagOptions(expenseTags), [expenseTags]);

  const enableMissingInfoButton = useMemo(() => {
    if (!displayMissingFieldIndicator) return false;
    if (!isAdvancedExpensesPlan) return false;

    const isExpenseReportedByEmployee = expense?.reported_by?.email === employeeEmail;
    const isDraft = expensesHelpers.isExpenseDraft(expense);
    return (isAdmin || isExpenseReportedByEmployee) && isDraft;
  }, [employeeEmail, expense, displayMissingFieldIndicator, isAdvancedExpensesPlan, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const agentSummary = useMemo(() => {
    const message = expense?.approval_summary || '';
    return { available: Boolean(message), message };
  }, [expense?.approval_summary]);

  useEffect(() => {
    if (expense) {
      const initialValues = {
        category_tags: expense?.category_tags?.[0] || undefined,
        description: expense?.description || '',
        location_tags: expense?.location_tags || [],
        policy_expense_type: expense?.policy_expense_type || undefined,
        project_tags: expense?.project_tags || [],
        tags: expense?.tags || [],
        team_tags: expense?.team_tags || [],
      };
      setInitialFormValues(initialValues);
      form.setFieldsValue(initialValues);
    }
  }, [expense]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTagList = (tagName) => {
    const tags = expense?.[tagName] || [];
    if (!tags || tags.length === 0) return '-';
    const names = tags.map(t => (typeof t === 'string' ? t : (t?.name || t?.label || ''))).filter(Boolean);
    return names.length ? names.join(', ') : '-';
  }

  const getTags = (tagName, tagFamily) => {
    // If user can edit we allow editing (select). Otherwise render read-only labels.
    if (canEdit) {
      const tags = initialFormValues?.[tagName] || [];
      return (
        <CompanyTagSelect
          autoLimit={true}
          className='expense-details-tags-select'
          onChange={(value) => handleEdit({[tagName]: value})}
          selected={tags}
          tagFamily={tagFamily}
          variant='button'
        />
      );
    }
    return renderTagList(tagName);
  }

  const handleSubmitForm = () => form.submit();

  const handlePressEnter = () => {
    handleSubmitForm();
    document.querySelectorAll(`#${formId} input`).forEach((input) => input.blur());
  }

  const handleSelectSuggestion = (suggestion) => {
    form.setFieldsValue({description: suggestion});
    handleSubmitForm();
  }

  const getMissingInfoLabel = (label, fieldName) => {
    const fieldValue = form.getFieldValue(fieldName);
    if (fieldValue || !displayMissingFieldIndicator) return label;

    return (
      <Space size='small' align='center' className='d-flex-child'>
        {label}
        <MissingFieldIndicator />
      </Space>
    );
  }

  const tableInfoData = [
    {
      key: 'description',
      label: getMissingInfoLabel(t('description'), 'description'),
      value: canEdit ? (
        <Form.Item name='description'>
          <SuggestionPopover onSelect={handleSelectSuggestion} key={expense?.id}>
            <Input.TextArea autoSize={{minRows: 1}} onBlur={handleSubmitForm} onPressEnter={handlePressEnter} />
          </SuggestionPopover>
        </Form.Item>
      ) : expense?.description || '-'
    },
    {
      key: 'amount',
      label: t('amount'),
      value: expensesHelpers.getExpensePrice(expense)
    },
    {
      key: 'expenseCategory',
      label: getMissingInfoLabel(t('category'), 'category_tags'),
      value: canEdit ? (
        <Form.Item name='category_tags'>
          <Select placeholder={t('choose')} options={expenseCategoryOptions} onChange={handleSubmitForm} />
        </Form.Item>
      ) : expense?.category_tags?.[0] || '-'
    },
    subscription && {
      key: 'subscription',
      label: t('subscription'),
      value: <SubscriptionIconWithLink subscription={subscription} />
    },
    {
      key: 'expense_date',
      label: t('date'),
      value: expensesHelpers.getFormattedExpenseDate(expense)
    },
    {
      key: 'paidBy',
      label: t('paidBy'),
      value: expense?.reported_by && (
        <UserDetails
          email={false}
          style={{display: 'flex', flexDirection: 'row-reverse'}}
          user={expense.reported_by}
        />
      )
    },
    {
      key: 'vendor',
      label: t('vendor'),
      value: expense?.vendor ? expensesHelpers.getExpenseVendorNameWithLink(expense, 'system') : '-'
    },
  ].filter(Boolean);

  const getTableCategoriesData = () => {
    let tableData = [];
    if (isAdmin || !isDraftExpense) {
      tableData = [
        {
          key: 'costCenter',
          label: t('costCenter'),
          value: getTags('tags', tagFamilyConstants.COST_CENTER)
        },
        {
          key: 'teamTags',
          label: t('team'),
          value: getTags('team_tags', tagFamilyConstants.TEAM)
        },
        {
          key: 'projectTags',
          label: t('project'),
          value: getTags('project_tags', tagFamilyConstants.PROJECT)
        },
        {
          key: 'locationTags',
          label: t('location'),
          value: getTags('location_tags', tagFamilyConstants.LOCATION)
        }
      ]
    }
    return tableData;
  }

  const handleEdit = (fields) => {
    const description = fields.description;
    const expenseDescription = expense?.description || '';

    // category_tags should be an array
    if (fields.category_tags && !Array.isArray(fields.category_tags)) {
      fields.category_tags = [fields.category_tags];
    }

    if (description === expenseDescription && fields.category_tags?.[0] === expense?.category_tags?.[0]) {
      return;
    }

    // If description is edited, we add it to suggestions for future use
    if (description && !expenseDescription && description !== expenseDescription) {
      expensesHelpers.addExpenseSuggestion(description);
    }

    onEdit?.(fields);
  }

  const handleOpenAgentChat = () => {
    dispatch(agentActions.openAgentChat({
      variant: agentTypeConstants.FILL_MISSING_INFO,
      query: { expense_id: expense?.id }
    }));
  }

  const tableCategoriesData = getTableCategoriesData();

  return (
    <StyledExpenseDetails {...rest}>
      {children}
      {agentSummary.available && <AgentAlert message={agentSummary.message} />}
      {!enableMissingInfoButton && (
        <Form
          id={formId}
          initialValues={initialFormValues}
          form={form}
          onFinish={handleEdit}
          requiredMark={false}
        >
          <DetailsTable data={tableInfoData} />
          {Boolean(tableCategoriesData.length) && (
            <>
              <Divider />
              <DetailsTable data={tableCategoriesData} />
            </>
          )}
        </Form>
      )}
      {enableMissingInfoButton && (
        <StyledExpenseDetailsButton
          icon={<StarsIcon />}
          onClick={handleOpenAgentChat}
          size='large'
          type='primary'
        >
          {t('fillMissingInfo')}
        </StyledExpenseDetailsButton>
      )}
    </StyledExpenseDetails>
  );
}

ExpenseDetails.propTypes = {
  availableLink: PropTypes.bool,
  displayMissingFieldIndicator: PropTypes.bool,
  expense: PropTypes.object,
  onEdit: PropTypes.func,
  subscription: PropTypes.object,
}

export default ExpenseDetails;
